/**
 * What is configured, what needs attention, and what is still optional — read
 * from real state, never from a flag that could go stale.
 *
 * Every area here is checked the same way the screen that actually manages it
 * checks it (`useDataSource`, `useOwnerCodesSummary`, `useAiCredential`), so
 * this can never claim a company is set up when the screen that owns that
 * area would say otherwise. Where the signed-in person lacks the capability
 * an area needs, that area is not queried at all — refusing to ask a
 * question that would come back "you may not see this" is the same rule
 * `WorkspaceHome` already followed, just applied consistently everywhere.
 */
import { useEffect, useMemo, useState } from 'react';
import { holds, type TenantContext } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { useDataSource } from '../datasource/useDataSource';
import { useOwnerCodesSummary } from '../reconciliation/useReconciliation';
import { useAiCredential } from '../ai/useAiCredential';
import { paths } from '../workspace/navigation';
import { useReceivablesFreshness } from '../receivables/useReceivablesFreshness';

export type SetupState = 'configured' | 'needs_attention' | 'optional' | 'not_configured';
export type SetupArea = 'source' | 'team' | 'ownerMappings' | 'stock' | 'reminders' | 'ai' | 'branding' | 'templates';

export interface SetupItem {
    area: SetupArea;
    label: string;
    required: boolean;
    state: SetupState;
    detail: string;
    /** Absent when the signed-in person may not act on this area at all. */
    href?: string;
    can: boolean;
}

interface Extras {
    memberCount: number | null;
    reminderEnabled: boolean | null;
    stockConnected: boolean | null;
    templateCount: number | null;
    loading: boolean;
}

/** The light, direct reads no dedicated screen hook already covers. */
function useExtras(context: TenantContext): Extras {
    const workspaceId = context.workspace.id;
    const canSeeTeam = holds(context, 'team.view') || holds(context, 'team.manage');
    const canSeeReminders = true; // workspace_alert_settings is readable by any member
    const canSeeStock = holds(context, 'datasource.view');
    const canSeeTemplates = true; // message_templates is readable by any member

    const [state, setState] = useState<Extras>({
        memberCount: null, reminderEnabled: null, stockConnected: null, templateCount: null, loading: true,
    });

    useEffect(() => {
        let current = true;
        setState(s => ({ ...s, loading: true }));
        void (async () => {
            // A count is a GET of no rows, never HEAD: PostgREST closes the connection after a HEAD without saying so,
            // and the gateway hands it to the next request of this burst, which then fails (tests/noHeadRequests).
            const [members, reminders, stock, templates] = await Promise.all([
                canSeeTeam
                    ? supabase().from('workspace_members').select('id', { count: 'exact' })
                        .eq('workspace_id', workspaceId).eq('status', 'active').limit(0)
                    : Promise.resolve({ count: null }),
                canSeeReminders
                    ? supabase().from('workspace_alert_settings').select('daily_email').eq('workspace_id', workspaceId).maybeSingle()
                    : Promise.resolve({ data: null }),
                canSeeStock
                    ? supabase().from('workspace_integrations').select('id, status').eq('workspace_id', workspaceId).eq('kind', 'stock').maybeSingle()
                    : Promise.resolve({ data: null }),
                canSeeTemplates
                    ? supabase().from('message_templates').select('id', { count: 'exact' }).eq('workspace_id', workspaceId).limit(0)
                    : Promise.resolve({ count: null }),
            ]);
            if (!current) return;
            setState({
                memberCount: (members as { count: number | null }).count,
                reminderEnabled: (reminders as { data: { daily_email: boolean } | null }).data?.daily_email ?? null,
                stockConnected: (stock as { data: { status: string } | null }).data ? true : null,
                templateCount: (templates as { count: number | null }).count,
                loading: false,
            });
        })();
        return () => { current = false; };
    }, [workspaceId, canSeeTeam, canSeeReminders, canSeeStock, canSeeTemplates]);

    return state;
}

export function useSetupStatus(context: TenantContext) {
    const { workspace } = context;
    const slug = workspace.slug;

    const canSource = holds(context, 'datasource.view');
    const { integration, runs, loading: sourceLoading } = useDataSource(canSource ? workspace.id : '');
    // A company on TallyPrime has no sheet: its source is its TallyPrime connection, and the answer every screen's
    // freshness line uses (receivables_freshness) says how that stands. Reading only the sheet told a connected
    // TallyPrime company "No sheet connected yet" (fixed 2026-10-14).
    const { freshness, unavailable: freshnessUnavailable } = useReceivablesFreshness(workspace.id);
    const tally = canSource && freshness?.source === 'tally' ? freshness : null;

    const canMappings = holds(context, 'team.manage');
    const { codes, loading: mappingsLoading } = useOwnerCodesSummary(canMappings ? workspace.id : '');

    const canAi = holds(context, 'integrations.manage');
    const { status: aiStatus, loading: aiLoading } = useAiCredential(canAi ? workspace.id : '');

    const extras = useExtras(context);

    const items = useMemo<SetupItem[]>(() => {
        const synced = runs.some(r => r.result === 'ok');
        // TallyPrime: disconnected; being set up (paired, or connected with no update yet); connected but something
        // needs attention (TallyPrime closed, updates failing…); or connected and updating.
        const tallyState: SetupState | null = !tally ? null
            : tally.problem === 'disconnected' ? 'not_configured'
                : tally.updatedAt === null || tally.problem !== null ? 'needs_attention' : 'configured';
        const tallyDetail = !tally ? null
            : tally.problem === 'disconnected' ? 'TallyPrime was disconnected. Connect it again in Receivables data.'
                : tally.updatedAt === null ? 'TallyPrime is being set up; its first update has not arrived yet.'
                    : tally.problem !== null ? 'TallyPrime is connected but needs attention: see Receivables data.'
                        : 'TallyPrime is connected and updating.';
        const sourceState: SetupState = !canSource ? 'not_configured'
            : tallyState ? tallyState
            : integration.status === 'unconfigured' ? 'not_configured'
                : integration.status === 'error' ? 'needs_attention'
                    : !synced ? 'needs_attention' : 'configured';

        const unresolved = codes.filter(c => !c.mappedMemberId).length;
        const list: SetupItem[] = [
            {
                area: 'source', label: 'Receivables data', required: true, can: canSource, state: sourceState,
                href: paths(slug).setting('data'),
                detail: !canSource ? 'Someone who manages receivables data can set this up.'
                    : tallyDetail ? tallyDetail
                    : integration.status === 'unconfigured' ? 'Not connected yet: TallyPrime or a Google Sheet.'
                        : integration.status === 'error' ? (integration.statusDetail ?? 'The last check failed.')
                            : !synced ? 'Connected, but no sync has completed yet.'
                                : 'Connected and synced.',
            },
            {
                area: 'team', label: 'Team', required: false, can: extras.memberCount !== null,
                state: (extras.memberCount ?? 1) > 1 ? 'configured' : 'optional',
                href: paths(slug).setting('team'),
                detail: extras.memberCount === null ? 'Someone with access to the team can see this.'
                    : extras.memberCount > 1 ? `${extras.memberCount} people have access.` : 'Just you, so far.',
            },
            {
                area: 'ownerMappings', label: 'Account owners', required: false, can: canMappings,
                state: !canMappings ? 'optional' : codes.length === 0 ? 'optional' : unresolved > 0 ? 'needs_attention' : 'configured',
                href: paths(slug).setting('ownership'),
                detail: !canMappings ? 'Someone who manages the team can see this.'
                    : codes.length === 0 ? (tally ? 'TallyPrime carries no owner codes. Give accounts their collector in Customers.' : 'No imported owner codes to map yet.')
                        : unresolved > 0 ? `${unresolved} imported code${unresolved === 1 ? '' : 's'} not yet mapped to a team member.`
                            : 'Every imported owner code is mapped.',
            },
            {
                area: 'stock', label: 'Stock sheet', required: false, can: canSource,
                state: !canSource ? 'optional' : extras.stockConnected ? 'configured' : 'optional',
                href: paths(slug).stock,
                detail: !canSource ? 'Someone who manages receivables data can see this.'
                    : extras.stockConnected ? 'A stock sheet is connected.' : 'Not connected. Optional.',
            },
            {
                area: 'reminders', label: 'Reminder emails', required: false, can: extras.reminderEnabled !== null,
                state: extras.reminderEnabled === null ? 'optional' : extras.reminderEnabled ? 'configured' : 'optional',
                href: paths(slug).setting('reminders'),
                detail: extras.reminderEnabled === null ? 'Not yet checked.'
                    : extras.reminderEnabled ? 'A daily digest is switched on.' : 'Switched off. Optional.',
            },
            {
                area: 'ai', label: 'AI summaries', required: false, can: canAi,
                state: !canAi ? 'optional' : aiStatus.configured ? 'configured' : 'optional',
                href: paths(slug).setting('ai'),
                detail: !canAi ? 'Someone who manages integrations can see this.'
                    : aiStatus.configured ? `Connected to ${aiStatus.provider ?? 'a provider'}.` : 'Not connected. Optional.',
            },
            {
                area: 'branding', label: 'Company logo', required: false, can: true,
                state: workspace.logoPath ? 'configured' : 'optional',
                href: paths(slug).setting('company'),
                detail: workspace.logoPath ? 'A logo is set.' : 'Using initials for now. Optional.',
            },
            {
                area: 'templates', label: 'Message templates', required: false, can: extras.templateCount !== null,
                state: extras.templateCount === null ? 'optional' : extras.templateCount > 0 ? 'configured' : 'optional',
                href: paths(slug).setting('templates'),
                detail: extras.templateCount === null ? 'Not yet checked.'
                    : extras.templateCount > 0 ? `${extras.templateCount} saved.` : 'None yet. Optional.',
            },
        ];
        return list;
    }, [canSource, integration, runs, codes, canMappings, extras, canAi, aiStatus, workspace.logoPath, slug, tally]);

    // Until the freshness answer arrives the source is not known, so a TallyPrime company never flashes the sheet's words.
    const freshnessLoading = canSource && freshness === null && !freshnessUnavailable;
    const loading = sourceLoading || freshnessLoading || mappingsLoading || aiLoading || extras.loading;
    const mandatoryDone = items.find(i => i.area === 'source')!.state === 'configured';

    return { items, loading, mandatoryDone };
}
